import { useState, useEffect, useCallback } from 'react';
import { fetchSessions, fetchSessionMessages, deleteSession, renameSession, startChatStream } from '../services/ragChatService';

/**
 * useRAGChat.js
 * Manages Velocity AI bot session list and per-session message history.
 *
 * @param {string|null} rfpId - The current RFP context ID
 * @param {boolean} isAITabActive - Whether the AI tab is currently visible
 */
export const useRAGChat = ({ rfpId, isAITabActive }) => {
    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [sessionsError, setSessionsError] = useState(null);

    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [messagesError, setMessagesError] = useState(null);
    const [sendingMessage, setSendingMessage] = useState(false);

    // ─── Load sessions when the AI tab becomes visible & rfpId is present ───────
    useEffect(() => {
        if (!rfpId || !isAITabActive) return;

        let cancelled = false;
        setLoadingSessions(true);
        setSessionsError(null);

        fetchSessions(rfpId)
            .then((data) => {
                if (cancelled) return;
                // Normalise: the API may return an array or { sessions: [...] } or { data: [...] }
                const list = Array.isArray(data) ? data : (data?.sessions ?? data?.data ?? []);
                setSessions(list);

                // Auto-select the most recent session if none selected
                if (list.length > 0 && !selectedSessionId) {
                    setSelectedSessionId(list[0]?.session_id ?? list[0]?.id ?? list[0]?._id);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('[useRAGChat] Failed to load sessions:', err);
                setSessionsError('Failed to load chat history.');
            })
            .finally(() => {
                if (!cancelled) setLoadingSessions(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rfpId, isAITabActive]);

    // ─── Load messages whenever the selected session changes ────────────────────
    useEffect(() => {
        if (!rfpId || !selectedSessionId || !isAITabActive) {
            if (!rfpId || !selectedSessionId) {
                setMessages([]);
            }
            return;
        }

        let cancelled = false;
        setLoadingMessages(true);
        setMessagesError(null);

        fetchSessionMessages(rfpId, selectedSessionId)
            .then((data) => {
                if (cancelled) return;
                // Normalise: array or { messages: [...] } or { data: [...] }
                const list = Array.isArray(data) ? data : (data?.messages ?? data?.data ?? []);
                setMessages(list);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('[useRAGChat] Failed to load messages:', err);
                setMessagesError('Failed to load conversation history.');
            })
            .finally(() => {
                if (!cancelled) setLoadingMessages(false);
            });

        return () => { cancelled = true; };
    }, [rfpId, selectedSessionId, isAITabActive]);

    /** Switch to a different session by ID */
    const selectSession = useCallback((sessionId) => {
        setSelectedSessionId(sessionId);
        setMessages([]);
    }, []);

    /** Start a fresh session locally (clearing context for a new conversation) */
    const startNewSession = useCallback(() => {
        setSelectedSessionId(null);
        setMessages([]);
    }, []);

    /** Delete a session by ID */
    const handleDeleteSession = useCallback(async (sessionId) => {
        if (!rfpId) return false;
        try {
            await deleteSession(rfpId, sessionId);
            // Update sessions list locally
            setSessions((prev) => {
                const nextList = prev.filter((s) => {
                    const sid = s?.session_id ?? s?.id ?? s?._id;
                    return String(sid) !== String(sessionId);
                });

                // If the deleted session was the currently selected one, select the next available one
                if (String(selectedSessionId) === String(sessionId)) {
                    if (nextList.length > 0) {
                        setSelectedSessionId(nextList[0]?.session_id ?? nextList[0]?.id ?? nextList[0]?._id);
                    } else {
                        setSelectedSessionId(null);
                        setMessages([]);
                    }
                }
                return nextList;
            });
            return true;
        } catch (err) {
            console.error('[useRAGChat] Failed to delete session:', err);
            return false;
        }
    }, [rfpId, selectedSessionId]);

    /** Rename a session by ID */
    const handleRenameSession = useCallback(async (sessionId, newTitle) => {
        if (!rfpId || !newTitle.trim()) return false;
        try {
            await renameSession(rfpId, sessionId, newTitle);
            // Update sessions list locally
            setSessions((prev) =>
                prev.map((s) => {
                    const sid = s?.session_id ?? s?.id ?? s?._id;
                    if (String(sid) === String(sessionId)) {
                        return { ...s, title: newTitle, session_name: newTitle, name: newTitle };
                    }
                    return s;
                })
            );
            return true;
        } catch (err) {
            console.error('[useRAGChat] Failed to rename session:', err);
            return false;
        }
    }, [rfpId]);

    /** Send a message using the RAG stream API and update UI in real-time */
    const sendMessage = useCallback(async (userMessage, sessionId = selectedSessionId) => {
        if (!rfpId || !userMessage.trim() || sendingMessage) return;

        setSendingMessage(true);

        // 1. Immediately append user's message to UI list
        const userMsgObj = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: userMessage
        };
        setMessages((prev) => [...prev, userMsgObj]);

        // 2. Append a placeholder message for the streaming AI reply
        const aiPlaceholderId = `ai-${Date.now()}`;
        const aiPlaceholderObj = {
            id: aiPlaceholderId,
            role: 'assistant',
            content: '',
            sources: null
        };
        setMessages((prev) => [...prev, aiPlaceholderObj]);

        // 3. Typewriter queue setup to feed the UI word-by-word/char-by-char
        let displayedText = '';
        const textQueue = [];

        // Timer that drains character queue at a polished, natural pace (15ms interval)
        const typewriterInterval = setInterval(() => {
            if (textQueue.length > 0) {
                // Type 3 characters at a time for a smooth, readable, yet fast presentation
                const nextChars = textQueue.splice(0, 3).join('');
                displayedText += nextChars;

                setMessages((prev) =>
                    prev.map((msg) =>
                        msg.id === aiPlaceholderId
                            ? { ...msg, content: displayedText }
                            : msg
                    )
                );
            }
        }, 15);

        try {
            const reader = await startChatStream(rfpId, userMessage, sessionId);
            const decoder = new TextDecoder("utf-8");
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunkText = decoder.decode(value, { stream: true });
                buffer += chunkText;

                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Buffer incomplete lines

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed.startsWith('data: ')) continue;

                    try {
                        const eventData = JSON.parse(trimmed.replace('data: ', ''));

                        if (eventData.type === 'chunk' && eventData.content) {
                            // Queue characters for typewriter rendering
                            textQueue.push(...eventData.content.split(''));
                        } else if (eventData.type === 'done') {
                            const finalSessionId = eventData.session_id;

                            // If a new session was created by the backend, select it and refresh sessions
                            if (finalSessionId && !sessionId) {
                                setSelectedSessionId(finalSessionId);
                                fetchSessions(rfpId).then((data) => {
                                    const list = Array.isArray(data) ? data : (data?.sessions ?? []);
                                    setSessions(list);
                                });
                            }
                        }
                    } catch (parseErr) {
                        // Fail-safe for partial JSON chunks
                    }
                }
            }

            // Keep checking and waiting until the queue is completely typed out
            while (textQueue.length > 0) {
                await new Promise((resolve) => setTimeout(resolve, 15));
                const nextChars = textQueue.splice(0, 4).join('');
                displayedText += nextChars;
                setMessages((prev) =>
                    prev.map((msg) =>
                        msg.id === aiPlaceholderId
                            ? { ...msg, content: displayedText }
                            : msg
                    )
                );
            }

        } catch (err) {
            console.error('[useRAGChat] Streaming failed:', err);
            // Show fallback error inside the bubble
            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === aiPlaceholderId
                        ? { ...msg, content: "Sorry, I encountered an issue generating a response." }
                        : msg
                )
            );
        } finally {
            clearInterval(typewriterInterval);
            setSendingMessage(false);
        }
    }, [rfpId, selectedSessionId, sendingMessage]);

    return {
        sessions,
        loadingSessions,
        sessionsError,
        selectedSessionId,
        selectSession,
        startNewSession,
        deleteSession: handleDeleteSession,
        renameSession: handleRenameSession,
        sendMessage,
        sendingMessage,
        messages,
        loadingMessages,
        messagesError,
    };
};
